// src/api/artistLineup.api.ts
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { doc, getDoc, getDocs, collection, query, where, orderBy, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { AUTH, DB, FUNCTIONS } from 'src/contexts/FirebaseContext';

import { ArtistLineupItem, SpotifyArtist, SpotifyArtistDetails } from 'src/@types/artist';

export const ArtistLineupApi = createApi({
  reducerPath: 'ArtistLineupApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['ArtistLineup', 'SpotifyArtist', 'ShareableLinks'],
  endpoints: (builder) => ({
    // Get all artists in an event lineup
    getEventArtists: builder.query<ArtistLineupItem[], string>({
      async queryFn(eventId) {
        try {
          const artistsRef = collection(DB, 'events', eventId, 'artistLineup');
          const q = query(artistsRef, orderBy('order', 'asc'));
          const querySnapshot = await getDocs(q);
          
          const artists: ArtistLineupItem[] = [];
          querySnapshot.forEach((doc) => {
            artists.push({
              id: doc.id,
              ...doc.data()
            } as ArtistLineupItem);
          });
          
          return { data: artists };
        } catch (error: any) {
          console.error('Error fetching artist lineup:', error);
          return { error: error.message };
        }
      },
      providesTags: ['ArtistLineup'],
    }),
    
    // Add an artist to the event lineup
    addArtistToLineup: builder.mutation<ArtistLineupItem, { eventId: string; artistData: Omit<ArtistLineupItem, 'id'> }>({
      async queryFn({ eventId, artistData }) {
        try {
            const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const addArtist = httpsCallable(FUNCTIONS, 'artistslineup-addArtistToLineup');
          const result = await addArtist({ eventId, artistData, idToken });
          return { data: result.data as ArtistLineupItem };
        } catch (error: any) {
          console.error('Error adding artist to lineup:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['ArtistLineup'],
    }),
    
    // Update an artist in the event lineup
    updateArtistInLineup: builder.mutation<ArtistLineupItem, { eventId: string; artistId: string; artistData: Partial<ArtistLineupItem> }>({
      async queryFn({ eventId, artistId, artistData }) {
        try {
            const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const updateArtist = httpsCallable(FUNCTIONS, 'artistslineup-updateArtistInLineup');
          const result = await updateArtist({ eventId, artistId, artistData, idToken });
          return { data: result.data as ArtistLineupItem };
        } catch (error: any) {
          console.error('Error updating artist in lineup:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['ArtistLineup'],
    }),
    
    // Remove an artist from the event lineup
    removeArtistFromLineup: builder.mutation<{ success: boolean }, { eventId: string; artistId: string }>({
      async queryFn({ eventId, artistId }) {
        try {
            const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const removeArtist = httpsCallable(FUNCTIONS, 'artistslineup-removeArtistFromLineup');
          const result = await removeArtist({ eventId, artistId, idToken });
          return { data: result.data as { success: boolean } };
        } catch (error: any) {
          console.error('Error removing artist from lineup:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['ArtistLineup'],
    }),
    
    // Reorder artists in the event lineup
    reorderArtistLineup: builder.mutation<{ success: boolean }, { eventId: string; artistOrders: Array<{ id: string; order: number }> }>({
      async queryFn({ eventId, artistOrders }) {
        try {
            const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const reorderLineup = httpsCallable(FUNCTIONS, 'artistslineup-reorderArtistLineup');
          const result = await reorderLineup({ eventId, artistOrders, idToken });
          return { data: result.data as { success: boolean } };
        } catch (error: any) {
          console.error('Error reordering artist lineup:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['ArtistLineup'],
    }),
    
    // Search for artists on Spotify
    searchSpotifyArtists: builder.query<SpotifyArtist[], string>({
      async queryFn(query) {
        try {
          if (!query || query.length < 2) {
            return { data: [] };
          }

          const { currentUser } = AUTH;
          if (!currentUser) {                
                return { data: [] };;
            }        
          const idToken = await currentUser.getIdToken();
                
          
          const searchArtists = httpsCallable(FUNCTIONS, 'artistslineup-searchSpotifyArtists');
          const result = await searchArtists({ 
            query, 
            idToken: idToken });
          return { data: result.data as SpotifyArtist[] };
        } catch (error: any) {
          console.error('Error searching Spotify artists:', error);
          return { error: error.message };
        }
      },
      providesTags: (result, error, query) => [{ type: 'SpotifyArtist', id: query }],
    }),
    
    // Get artist details from Spotify
    getSpotifyArtistDetails: builder.query<SpotifyArtistDetails, string>({
      async queryFn(artistId) {
        try {
          const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const getArtistDetails = httpsCallable(FUNCTIONS, 'artistslineup-getSpotifyArtistDetails');
          const result = await getArtistDetails({ artistId, idToken });
          return { data: result.data as SpotifyArtistDetails };
        } catch (error: any) {
          console.error('Error getting Spotify artist details:', error);
          return { error: error.message };
        }
      },
      providesTags: (result, error, artistId) => [{ type: 'SpotifyArtist', id: artistId }],
    }),
    
    // Generate a shareable link for music platforms
    generateShareableLink: builder.mutation<{ bookingUrl: string; apiUrl: string; token: string }, string>({
      async queryFn(eventId) {
        try {
            const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const generateLink = httpsCallable(FUNCTIONS, 'artistslineup-generateMusicPlatformShareableLink');
          const result = await generateLink({ eventId, idToken });
          console.log('Generated shareable link:', result.data);
          return { data: result.data as { bookingUrl: string; apiUrl: string; token: string } };
        } catch (error: any) {
          console.error('Error generating shareable link:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['ShareableLinks'],
    }),
    
    // Get existing shareable links for an event
    getShareableLinks: builder.query<{ bookingUrl: string; apiUrl: string; token: string } | null, string>({
      async queryFn(eventId) {
        try {
          const eventDoc = await getDoc(doc(DB, 'events', eventId));
          const eventData = eventDoc.data();
          
          if (eventData?.musicPlatformShareToken && eventData?.musicPlatformShareUrl && eventData?.musicPlatformApiUrl) {
            return { 
              data: {
                bookingUrl: eventData.musicPlatformShareUrl,
                apiUrl: eventData.musicPlatformApiUrl,
                token: eventData.musicPlatformShareToken
              }
            };
          }
          
          return { data: null };
        } catch (error: any) {
          console.error('Error getting shareable links:', error);
          return { error: error.message };
        }
      },
      providesTags: ['ShareableLinks'],
    }),
    
    // Notify artist subscribers
    notifyArtistSubscribers: builder.mutation<{ success: boolean; message: string }, string>({
      async queryFn(eventId) {
        try {
            const { currentUser } = AUTH;
          if (!currentUser) {                
                return { error: "No user logged in" };;
            }        
          const idToken = await currentUser.getIdToken();
          const notifySubscribers = httpsCallable(FUNCTIONS, 'artistslineup-notifyArtistSubscribers');
          const result = await notifySubscribers({ eventId, idToken });
          return { data: result.data as { success: boolean; message: string } };
        } catch (error: any) {
          console.error('Error notifying artist subscribers:', error);
          return { error: error.message };
        }
      },
    }),
  }),
});

export const {
  useGetEventArtistsQuery,
  useAddArtistToLineupMutation,
  useUpdateArtistInLineupMutation,
  useRemoveArtistFromLineupMutation,
  useReorderArtistLineupMutation,
  useSearchSpotifyArtistsQuery,
  useLazySearchSpotifyArtistsQuery,
  useGetSpotifyArtistDetailsQuery,
  useLazyGetSpotifyArtistDetailsQuery,
  useGenerateShareableLinkMutation,
  useGetShareableLinksQuery,
  useNotifyArtistSubscribersMutation
} = ArtistLineupApi;